<script setup lang="ts">
import { computed, defineAsyncComponent, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { isResolvedChatStory } from '../types/content'
import { resolveLanding, resolvePreview } from '../content/registry'

const StoryReader = defineAsyncComponent(() => import('../components/StoryReader.vue'))
const ChatStory = defineAsyncComponent(() => import('../components/ChatStory.vue'))

const route = useRoute()

function queryValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

const reader = computed(() => {
  const landingId = queryValue(route.params.landingId)
  if (landingId) return resolveLanding(landingId)
  return resolvePreview(String(route.params.storySlug), queryValue(route.query.v), queryValue(route.query.lock), queryValue(route.query.start))
})

watchEffect(() => {
  document.title = reader.value ? `${reader.value.story.title} · Povli` : 'Story not found · Povli'
})

watchEffect((onCleanup) => {
  const experience = reader.value
  if (!experience || !isResolvedChatStory(experience)) return
  const firstNode = experience.story.nodes.find((node) => node.id === experience.startNode)
  if (firstNode?.type !== 'image') return
  const preload = document.createElement('link')
  preload.rel = 'preload'
  preload.as = 'image'
  preload.href = firstNode.src
  preload.setAttribute('fetchpriority', 'high')
  document.head.append(preload)
  onCleanup(() => preload.remove())
})
</script>

<template>
  <Suspense v-if="reader">
    <ChatStory v-if="isResolvedChatStory(reader)" :key="`${reader.story.story_id}:${reader.story.version}:${reader.lockNode}`" :story="reader" />
    <StoryReader v-else :key="`${reader.story.story_id}:${reader.story.version}:${reader.lock.id}`" :reader="reader" />
    <template #fallback>
      <div class="grid min-h-dvh place-items-center bg-paper px-6 text-center text-ink">
        <p class="text-xs font-bold uppercase tracking-[0.2em] text-accent">Opening story&hellip;</p>
      </div>
    </template>
  </Suspense>
  <div v-else class="grid min-h-dvh place-items-center bg-paper px-6 text-center text-ink">
    <div>
      <p class="text-xs font-bold uppercase tracking-[0.2em] text-accent">404</p>
      <h1 class="mt-4 font-serif text-4xl font-semibold">This story isn’t available.</h1>
      <p class="mt-3 text-muted">Please check the link or ask for a new one.</p>
    </div>
  </div>
</template>
